/* ============================================================================
 * COMMUNITY.EXE — the people on the list
 * ----------------------------------------------------------------------------
 * Ported from the design source (SupercomputeA/supercompute-immersion →
 * app-community.html): the member roll, tiers, invites and perks.
 *
 * WHAT IS REAL: invites issued here are held in this browser. The member card
 * opens from a roll row.
 * WHAT IS NOT, AND SAYS SO: the roll and tiers are a specimen sheet — sample
 * handles, not a live directory. Handles are the only identity the desk keeps.
 * ========================================================================= */

import { useState } from 'react';
import { SkinArt } from '../components/SkinArt';

const TABS = [
  { id: 'members', label: 'Members' },
  { id: 'tiers', label: 'Tiers' },
  { id: 'invites', label: 'Invites' },
  { id: 'perks', label: 'Perks' },
] as const;
type Tab = (typeof TABS)[number]['id'];

interface Member {
  no: string;
  handle: string;
  tier: string;
  roles: string;
  notes: string;
  since: string;
  active: boolean;
}

const MEMBERS: readonly Member[] = [
  { no: 'M-001', handle: 'fleet-hand', tier: 'founder', roles: 'founder · press', notes: 'Runs the Lisbon room.', since: '2013', active: true },
  { no: 'M-014', handle: 'brass-key', tier: 'guild', roles: 'guild · school', notes: 'Signed the guestbook at Kessel.', since: '2024', active: true },
  { no: 'M-021', handle: 'harbour', tier: 'guild', roles: 'guild', notes: 'Lisbon cohort 01.', since: '2025', active: true },
  { no: 'M-033', handle: 'studio-nine', tier: 'press', roles: 'press', notes: 'Wrote up the Milan session.', since: '2026', active: false },
  { no: 'M-041', handle: 'field-notes', tier: 'school', roles: 'school · guild', notes: 'Cohort 02, security track.', since: '2026', active: true },
];

const TIERS = [
  { tier: 'Founder', gate: 'invite, manual', holds: 3, costs: 'nothing' },
  { tier: 'Guild', gate: 'credential · event-attendance', holds: 48, costs: 'nothing' },
  { tier: 'School', gate: 'credential · school-completion', holds: 12, costs: 'cohort seat' },
  { tier: 'Press', gate: 'manual', holds: 4, costs: 'nothing' },
];

const PERKS = [
  { label: 'Room before public', tier: 'all tiers' },
  { label: 'School seats', tier: 'school' },
  { label: 'Guestbook and memories', tier: 'guild' },
  { label: 'First look at merch drops', tier: 'guild' },
  { label: 'Newsroom byline', tier: 'press' },
];

const INVITE_TIERS = ['guild', 'school', 'press'] as const;

interface Invite {
  code: string;
  tier: string;
}

function readInvites(): Invite[] {
  try {
    return JSON.parse(localStorage.getItem('sc.community.invites') ?? '[]') as Invite[];
  } catch {
    return [];
  }
}

export function Community() {
  const [tab, setTab] = useState<Tab>('members');
  const [selected, setSelected] = useState<Member | null>(null);
  const [filter, setFilter] = useState('');
  const [invites, setInvites] = useState<Invite[]>(readInvites);
  const [invTier, setInvTier] = useState<string>(INVITE_TIERS[0]);
  const [invNote, setInvNote] = useState('');
  const [merchSwitch, setMerchSwitch] = useState(true);
  const [guestbookSwitch, setGuestbookSwitch] = useState(false);

  const visible = MEMBERS.filter((m) =>
    (m.handle + ' ' + m.tier + ' ' + m.no).toLowerCase().includes(filter.toLowerCase()),
  );

  const issueInvite = () => {
    const note = invNote.trim();
    if (!note) return;
    const code = `SC-${invTier.slice(0, 2).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    const next = [{ code, tier: invTier }, ...invites].slice(0, 12);
    setInvites(next);
    try {
      localStorage.setItem('sc.community.invites', JSON.stringify(next));
    } catch {
      /* non-fatal */
    }
    setInvNote('');
  };

  const mono = (h: string) => h.replace(/[^a-z]/gi, '').slice(0, 2).toUpperCase() || '—';

  return (
    <div className="dsp page">
      <header className="page__head">
        <SkinArt className="page__crest" src="/logos/dock-community.png" width={40} height={40} alt="" aria-hidden="true" />
        <div className="page__titles">
          <p className="eyebrow">community member</p>
          <h1 className="h1">The people on the list</h1>
        </div>
        <div className="page__chips">
          <span className="chip">4 tiers</span>
        </div>
      </header>

      <div className="tabs page__tabs" role="tablist" aria-label="Community modules">
        {TABS.map((t) => (
          <button key={t.id} role="tab" type="button" aria-selected={tab === t.id} tabIndex={tab === t.id ? 0 : -1} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ MEMBERS ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'members'}>
        <div className="grid3">
          <div className="stat-tile"><span className="label">members</span><span className="val">48</span><span className="sub">On the roll.</span></div>
          <div className="stat-tile"><span className="label">guild</span><span className="val">48</span><span className="sub">Attendance-earned.</span></div>
          <div className="stat-tile"><span className="label">school</span><span className="val">12</span><span className="sub">Completed a cohort.</span></div>
        </div>
        <div className="row row--between">
          <div className="grow"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>the roll</p><p className="cap">Select a row to open the member card. Handles are the only identity the desk keeps.</p></div>
          <span className="mono">{visible.length} rows</span>
        </div>
        <input className="field" type="search" placeholder="filter members" aria-label="Filter members" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>No.</th><th>Handle</th><th>Tier</th><th className="r">Joined</th><th>State</th></tr></thead>
            <tbody>
              {visible.map((m) => (
                <tr key={m.no} tabIndex={0} onClick={() => setSelected(m)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setSelected(m); } }}>
                  <td className="mono">{m.no}</td>
                  <td>{m.handle}</td>
                  <td><span className="chip">{m.tier}</span></td>
                  <td className="r mono">{m.since}</td>
                  <td>{m.active ? <span className="badge badge-live"><span className="lamp" aria-hidden="true" />active</span> : <span className="tag tag--muted">unverified</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {selected && (
          <div className="panel stack" style={{ gap: 4 }}>
            <div className="row">
              <span className="monogram" aria-hidden="true">{mono(selected.handle)}</span>
              <div className="grow"><p className="eyebrow">{selected.no}</p><p className="mono mono--ink">{selected.handle}</p></div>
            </div>
            <dl className="kv">
              <dt>tier</dt><dd>{selected.tier}</dd>
              <dt>roles</dt><dd>{selected.roles || '—'}</dd>
              <dt>member since</dt><dd>{selected.since}</dd>
            </dl>
            <p className="cap">{selected.notes}</p>
            <div className="row row--between foot">
              <span className="tag tag--muted">sample data</span>
              <button className="btn btn--ghost" type="button" onClick={() => setSelected(null)}>Close card</button>
            </div>
          </div>
        )}
        <div className="row row--between"><span className="tag tag--muted">sample data</span><span className="cap">Handles, not names. The desk keeps the minimum it can act on.</span></div>
      </section>

      {/* ══ TIERS ══════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'tiers'}>
        <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>tiers</p><span className="tag tag--muted">sample data</span></div>
        <div className="tblwrap">
          <table className="tbl">
            <thead><tr><th>Tier</th><th>Gate</th><th className="r">Holds</th><th className="r">Costs</th></tr></thead>
            <tbody>
              {TIERS.map((t, i) => (
                <tr key={i}><td>{t.tier}</td><td>{t.gate}</td><td className="r mono">{t.holds}</td><td className="r mono">{t.costs}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="cap">Membership is earned at a room or a cohort, not bought from a page.</p>
      </section>

      {/* ══ INVITES ════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'invites'}>
        <div className="grid2">
          <div className="stack">
            <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>issue an invite</p>
            <div>
              <label className="lbl micro" htmlFor="ivTier">Tier</label>
              <select className="field" id="ivTier" value={invTier} onChange={(e) => setInvTier(e.target.value)}>
                {INVITE_TIERS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl micro" htmlFor="ivNote">For whom</label>
              <input className="field" id="ivNote" type="text" placeholder="a handle, or a note to self" value={invNote} onChange={(e) => setInvNote(e.target.value)} />
            </div>
            <button className="btn btn--gold" type="button" onClick={issueInvite} disabled={invNote.trim().length < 2}>Issue invite</button>
          </div>
          <div className="stack">
            <div className="row row--between"><p className="eyebrow" style={{ color: 'var(--ui-text)' }}>invites</p><span className="mono">{invites.length}</span></div>
            {invites.length === 0 ? (
              <p className="empty">No invites issued from this desk.</p>
            ) : (
              <ul className="rows" style={{ border: 'var(--ui-bw) solid var(--ui-border)', borderRadius: 'var(--ui-radius-ctl)', background: 'var(--ui-canvas)', overflow: 'auto', maxHeight: 260 }}>
                {invites.map((inv, i) => (
                  <li key={i} className="lrow"><span className="mono mono--ink">{inv.code}</span><span className="tag tag--muted">{inv.tier}</span></li>
                ))}
              </ul>
            )}
            <p className="cap">A code is a draft until it is written where invites are honoured.</p>
          </div>
        </div>
      </section>

      {/* ══ PERKS ══════════════════════════════════════════════════════════ */}
      <section className="panel" hidden={tab !== 'perks'}>
        <p className="eyebrow" style={{ color: 'var(--ui-text)' }}>what members get</p>
        <ul className="rows" style={{ border: 'var(--ui-bw) solid var(--ui-border)', borderRadius: 'var(--ui-radius-ctl)', background: 'var(--ui-canvas)' }}>
          {PERKS.map((p, i) => (
            <li key={i} className="lrow"><span className="lrow__t">{p.label}</span><span className="tag tag--muted">{p.tier}</span></li>
          ))}
        </ul>
        <div className="grid2">
          <div className="term row row--between">
            <div className="grow"><p className="micro micro--ink">Early merch access</p><p className="cap">Members see the line a day before the storefront does.</p></div>
            <button className="switch" type="button" aria-pressed={merchSwitch} aria-label="Early merch access" onClick={() => setMerchSwitch((v) => !v)}>
              <span className="switch__track"><span className="switch__knob" /></span>
            </button>
          </div>
          <div className="term row row--between">
            <div className="grow"><p className="micro micro--ink">Guestbook is guild-only</p><p className="cap">Anyone can read; only members can sign.</p></div>
            <button className="switch" type="button" aria-pressed={guestbookSwitch} aria-label="Guestbook is guild-only" onClick={() => setGuestbookSwitch((v) => !v)}>
              <span className="switch__track"><span className="switch__knob" /></span>
            </button>
          </div>
        </div>
      </section>

      <p className="page__foot">A member is a handle, a tier, and the rooms they turned up to.</p>
    </div>
  );
}
